import { FC, useRef } from 'react';
import Page, { PageProps } from '@/components/page';
import { Box, Stack } from '@mui/material';
import { DataTableInstance, DtColumn, DtFilter, DtPage } from '@/components/data-table/helpers';
import Icon from '@/components/icon';
import PageHeader from '@/components/page/header';
import DataTable from '@/components/data-table';
import { Navigate, useNavigate } from '@tanstack/react-router';
import Text from '@/components/text';
import { createScopedPolicy, getScopedPolicies } from '@/lib/services/policy.service';
import useDrawer from '@/hooks/use-drawer.hook';
import { PolicyModel } from '@/lib/models/policy-model/policy.model';
import useToast from '@/hooks/use-toast.hook';
import Button from '@/components/button';
import { QueryKey } from '@/lib/query-client';
import DomSize from '@/components/dom-size';
import useFeature from '@/hooks/use-feature.hook';
import Tooltip from '@/components/tooltip';
import PageFeatureToast from '@/components/page-feature-toast';
import PageBlurb from '@/components/page/blurb';
import PageTools from '@/components/page/tools';

const columns: DtColumn[] = [
  { name: 'name', label: 'Name', flex: 2, sortable: true },
  { name: 'action', label: '', sortable: false },
];

const PoliciesPage: FC<PageProps> = () => {
  const dtRef = useRef<DataTableInstance>(null);
  const navigate = useNavigate();
  const { openDrawer, closeDrawer } = useDrawer();
  const { errorToast } = useToast();
  const { canChangePolicy, getTooltip, hasPolicyV2 } = useFeature();

  const handleView = (id: string) => {
    navigate({
      to: '/config/policy/scoped/$policyId',
      params: { policyId: id },
    });
  };

  const handleAdd = () => {
    openDrawer('new-with-name', {
      title: 'Create New Scoped Policy',
      content: 'Please enter a name for the policy',
      persistToUrl: false,
      onSubmit: ({ name }: { name: string }) => {
        createScopedPolicy(new PolicyModel({ name })).then(({ created, model }) => {
          if (created) {
            closeDrawer();
            navigate({
              to: '/config/policy/scoped/$policyId',
              params: { policyId: model!.id },
            });
            return;
          }

          errorToast('The scoped policy was not created');
        });
      },
    });
  };

  const loadData = async (pageParams: DtPage, filter?: DtFilter) => {
    const { page, pageSize, sort } = pageParams;
    const dataset = await getScopedPolicies(page, pageSize, sort, filter);

    return {
      ...dataset,
      rows: dataset.rows.map(({ id, name, description }) => {
        const hasDescription = !!description;
        return {
          id,
          name: (
            <Box maxWidth="100%">
              <Box>{name}</Box>
              {hasDescription && (
                <Box pt={1}>
                  <Text color="grey" size="small" dotdot>
                    {description}
                  </Text>
                </Box>
              )}
            </Box>
          ),
          action: (
            <Stack direction="row" width="100%" justifyContent="flex-end">
              <Icon name="arrow-right" onClick={() => handleView(id)} />
            </Stack>
          ),
        };
      }),
    };
  };

  if (!hasPolicyV2) {
    return <Navigate to="/config/group-policies" />;
  }

  return (
    <Page title="Scoped Policies">
      <PageHeader>
        <PageBlurb>
          Create new scoped policies, and view and change permissions for existing policies.
        </PageBlurb>
        <PageTools>
          <Tooltip title={getTooltip('add-policy')} disabled={canChangePolicy}>
            <Button
              icon="plus"
              label="Add Policy"
              size="small"
              onClick={handleAdd}
              disabled={!canChangePolicy}
            />
          </Tooltip>
        </PageTools>
      </PageHeader>
      <PageFeatureToast featureId="change-policy" can={canChangePolicy} />
      <DomSize>
        <DataTable
          queryKey={QueryKey.ScopedPoliciesDataset}
          columns={columns}
          onLoad={loadData}
          ref={dtRef}
          searchPlaceholder="Search by name or description"
          search
          sort={{ columnName: 'name', direction: 'asc' }}
          rowHeight={70}
        />
      </DomSize>
    </Page>
  );
};

export default PoliciesPage;
